import { useTranslation } from 'react-i18next'
import { MapPin, List, Search } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { iconNameToEmoji } from './constants'

interface MapHeaderProps {
  viewMode: 'map' | 'list'
  onViewModeChange: (mode: 'map' | 'list') => void
  searchTerm: string
  onSearchChange: (term: string) => void
  selectedCategory: string
  onCategoryChange: (categoryId: string) => void
  categoryOptions: any[]
  minRating: number | null
  onMinRatingChange: (rating: number | null) => void
  onlyPromotions: boolean
  onPromotionsChange: (value: boolean) => void
}

export function MapHeader({
  viewMode,
  onViewModeChange,
  searchTerm,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categoryOptions,
  minRating,
  onMinRatingChange,
  onlyPromotions,
  onPromotionsChange,
}: MapHeaderProps) {
  const { t } = useTranslation()

  return (
    <div className="bg-white border-b border-gray-100 p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-text-primary">{t('navigation.merchants')}</h1>
        <div className="flex items-center space-x-2">
          <Button
            variant={viewMode === 'map' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('map')}
          >
            <MapPin className="h-4 w-4 mr-2" />
            {t('merchants.map')}
          </Button>
          <Button
            variant={viewMode === 'list' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('list')}
          >
            <List className="h-4 w-4 mr-2" />
            {t('merchants.list')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-secondary" />
        <Input
          type="text"
          placeholder={t('merchants.searchMerchants')}
          value={searchTerm}
          onChange={onSearchChange}
          className="pl-10"
        />
      </div>

      {/* Categories */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        {categoryOptions.map((category) => {
          const isSelected = selectedCategory === category.id
          // Check if icon_url is a valid URL
          const iconUrl = category.icon_url && typeof category.icon_url === 'string' && (category.icon_url.startsWith('http') || category.icon_url.startsWith('/')) ? category.icon_url : null
          // Get emoji from icon name if it's not a URL
          const iconName = category.icon || '📍'
          const emoji = iconUrl ? null : (iconNameToEmoji[iconName.toLowerCase()] || iconName || '📍')
          return (
            <Button
              key={category.id}
              variant={isSelected ? 'primary' : 'outline'}
              size="sm"
              onClick={() => onCategoryChange(category.id)}
              className={`whitespace-nowrap ${!isSelected && category.color ? '' : ''}`}
            >
              <span className="mr-2 inline-flex items-center">
                {iconUrl ? (
                    <img src={iconUrl} alt="" className="w-4 h-4 object-contain" onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                    const parent = (e.target as HTMLImageElement).parentElement
                    if (parent) parent.innerHTML = emoji
                  }} />
                ) : (
                  emoji
                )}
              </span>
              {category.name}
            </Button>
          )
        })}
      </div>

      {/* Extra Filters */}
      <div className="flex items-center space-x-2 mt-2">
        <select
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
          value={minRating ?? ''}
          onChange={(e) => onMinRatingChange(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{t('common.all') || 'Todos'}</option>
          <option value="3">3+ ⭐</option>
          <option value="4">4+ ⭐</option>
          <option value="4.5">4.5+ ⭐</option>
        </select>
        <label className="inline-flex items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={onlyPromotions}
            onChange={(e) => onPromotionsChange(e.target.checked)}
          />
          <span>{t('merchants.promotions') || 'Promoções'}</span>
        </label>
      </div>
    </div>
  )
}

